import React, { useCallback, useEffect, useRef, useState } from "react";

//@ts-ignore
import Ico from "images/ico.svg";

type Props = {
  nameInput: string;
  title: string;
  message?: string;
  confirmButtonText?: string;
}

export const PHOTO_CAPTURE_CONFIRM_BUTTON_PRESSED_EVENT = 'PhotoCapture-ConfirmButtonPressed';
export const PHOTO_CAPTURE_SHOW_MODAL = 'PhotoCapture-ShowModal';
export const PHOTO_CAPTURE_CLOSE_MODAL = 'PhotoCapture-CloseModal';

/**
 * Componente responsavel por abrir um modal para captura de foto
 * Ao capturar uma foto e clicar no botão confirmar, um custom event 'PhotoCapture-ConfirmButtonPressed' é disparado
 * Para abrir o modal, um custom Event 'PhotoCapture-ShowModal' deve ser disparado.
 * Também é possivel fechar o modal disparando o custom Event 'PhotoCapture-CloseModal'
 */
export function PhotoCapture({
  nameInput,
  title,
  message,
  confirmButtonText = 'Enviar'
}: Props) {
  const width = useRef<number>(640);
  const height = useRef<number>(480);
  const streaming = useRef<boolean>(false);
  const video = useRef<any>();
  const canvas = useRef<any>();
  const photo = useRef<any>();

  const [photoTaken, setPhotoTaken] = useState<any>();
  const [error, setError] = useState<string>();

  const showViewLiveResultButton = () => {
    if (window.self !== window.top) {
      // Ensure that if our document is in a frame, we get the user
      // to first open it in its own tab or window. Otherwise, it
      // won't be able to request permission for camera access.
      document.querySelector(".contentarea")?.remove();
      const button = document.createElement("button");
      button.textContent = "View live result of the example code above";
      document.body.append(button);
      button.addEventListener("click", () => window.open(location.href));
      return true;
    }
    return false;
  }

  const startUp = async () => {
    if (showViewLiveResultButton()) {
      return;
    }
    video.current = document.getElementById("video");
    canvas.current = document.getElementById("canvas");
    photo.current = document.getElementById("photo");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: {
        facingMode: 'user'
      }, audio: false });
      video.current.srcObject = stream;
      video.current.setAttribute("autoplay", ""); // corrige foto no iphone
      video.current.setAttribute("playsinline", ""); // corrige foto no iphone
      video.current.setAttribute("muted", ""); // corrige foto no iphone
      video.current.play();
    } catch (err) {
      console.error(`An error occurred: ${err}`);
      if ((err as Error).message == 'NotAllowedError: Permission denied') {
        setError("Você precisa conceder permissão de acesso a câmera, depois recarregue a página");
      } else {
        setError("Não foi possível inicializar a câmera do seu dispositivo")
      }
    }

    video.current?.addEventListener(
      "canplay",
      (ev: any) => {
        if (!streaming.current) {
          height.current = video.current.videoHeight / (video.current.videoWidth / width.current);

          // Firefox currently has a bug where the height can't be read from
          // the video, so we will make assumptions if this happens.

          if (isNaN(height.current)) {
            height.current = width.current / (4 / 3);
          }

          video.current.setAttribute("width", width.current);
          video.current.setAttribute("height", height.current);
          video.current.setAttribute("autoplay", ""); // corrige foto no iphone
          video.current.setAttribute("playsinline", ""); // corrige foto no iphone
          video.current.setAttribute("muted", ""); // corrige foto no iphone
          canvas.current.setAttribute("width", width.current);
          canvas.current.setAttribute("height", height.current);
          
          canvas.current.style.display = "none";
          streaming.current = true;
        }
      },
      false,
    );

    clearPhoto();
  }

  const stopCamera = useCallback(() => {
    if (video.current) {
      const stream = video.current.srcObject;
      if (stream) {
        stream.getTracks().forEach((track: any) => track.stop());
      }
      video.current.srcObject = null;
      video.current = null;
      streaming.current = false;
    }
  }, []);

  // Fill the photo with an indication that none has been
  // captured.
  const clearPhoto = () => {
    const context = canvas.current.getContext("2d");
    context.fillStyle = "#AAA";
    context.fillRect(0, 0, canvas.current.width, canvas.current.height);

    const data = canvas.current.toDataURL("image/png");
    photo.current.setAttribute("src", data);
  }

  // Capture a photo by fetching the current contents of the video
  // and drawing it into a canvas, then converting that to a PNG
  // format data URL. By drawing it on an offscreen canvas and then
  // drawing that to the screen, we can change its size and/or apply
  // other changes before drawing it.
  const takePicture = () => {
    const context = canvas.current.getContext("2d");
    if (width.current && height.current) {
      canvas.current.width = width.current;
      canvas.current.height = height.current;
      context.drawImage(video.current, 0, 0, width.current, height.current);

      const data = canvas.current.toDataURL("image/png");
      photo.current.setAttribute("src", data);
      photo.current.classList.add('effect');
      setTimeout(() => {
        photo.current.classList.remove('effect');
      }, 200);
      console.log(data);
      setPhotoTaken(data);
    } else {
      clearPhoto();
    }
  }

  const openModal = useCallback(() => {
    const modalFoto: HTMLDialogElement | undefined = document.getElementById('modal-foto') as HTMLDialogElement | undefined;
    if (modalFoto) {
      modalFoto.showModal();
      startUp();
    } else {
      console.error('Modal não encontrado');
      alert('Ops, houve um erro. Recarregue a pagina e tente novamente');
    }
  }, []);

  const closeModal = useCallback(() => {
    const modalFoto: HTMLDialogElement | undefined = document.getElementById('modal-foto') as HTMLDialogElement | undefined;
    if (modalFoto) {
      modalFoto.close();
      clearPhoto();
      stopCamera();
    }
  }, [clearPhoto, stopCamera]);

  const cancel = useCallback(() => {
    setPhotoTaken(undefined);
    closeModal();
  }, [setPhotoTaken, closeModal]);

  const onConfirm = useCallback(() => {
    closeModal();
    const event = new CustomEvent(PHOTO_CAPTURE_CONFIRM_BUTTON_PRESSED_EVENT);
    document.dispatchEvent(event);
  }, [closeModal]);

  useEffect(() => {
    document.addEventListener(PHOTO_CAPTURE_SHOW_MODAL, openModal);
    document.addEventListener(PHOTO_CAPTURE_CLOSE_MODAL, closeModal);

    const modalFoto: HTMLDialogElement | undefined = document.getElementById('modal-foto') as HTMLDialogElement | undefined;
    if (modalFoto) {
      modalFoto.addEventListener('close', closeModal);
    }

    return () => {
      document.removeEventListener(PHOTO_CAPTURE_SHOW_MODAL, openModal);
      document.removeEventListener(PHOTO_CAPTURE_CLOSE_MODAL, closeModal);
      modalFoto?.removeEventListener('close', closeModal);
    }
  }, []);

  return (
    <>
      <dialog id='modal-foto' className='dialog'>
        <div className='dialog__window'>
          <header className='header'>
            <h1 className='title'>Enviar foto</h1>
            <button className="close" type='button' onClick={cancel}>
            <svg className="ico"><use xlinkHref={`${Ico}#close`} /></svg>
            </button>
          </header>
          <div className="body">
            <p className="mensagem mensagem--alerta alinha-centraliza texto-2x-maior">{title}</p>
            {message && <p className="mensagem mensagem--alerta alinha-centraliza texto-2x-maior">{message}</p>}
            {error && (
            <p className="mensagem mensagem--erro alinha-centraliza texto-2x-maior">{error}</p>
          )}
            <div className="capture-container">
            <div className="camera">
              <video id="video" playsinline>Video não disponível</video>
            </div>
            <div className="output">
              <img id="photo" />
            </div>
            <canvas id="canvas"></canvas>
            </div>
        </div>
        <footer className="footer">
          {
            !error && (
              <button className="bt" id="startbutton" type='button' onClick={takePicture}>Tirar foto</button>
            )
          }
          {
            !error && (
              <button className="bt" type='button' onClick={onConfirm} disabled={!photoTaken}>{confirmButtonText}</button>
            )
          }
        </footer>
        </div>
      </dialog>
      <div>
        {photoTaken && <input type='hidden' name={nameInput} value={photoTaken} />}
      </div>
    </>
  )
}